import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Info, Plus, Trash2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useAdminDepartments } from '@/hooks/useAdminDepartments'
import type { IssueReason } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Field'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

/**
 * The preset lists behind the staff "Flag issue" picker, kept like the
 * checklist: one list per section, and staff only see their own section's.
 * Order here is picker order; inactive reasons are hidden from staff but stay
 * on old issues.
 */
export default function AdminIssueReasons() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [departmentId, setDepartmentId] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [deleting, setDeleting] = useState<IssueReason | null>(null)

  const departments = useAdminDepartments()

  const reasons = useQuery({
    queryKey: ['admin-issue-reasons'],
    queryFn: async () => (await api.get<{ data: IssueReason[] }>('/admin/issue-reasons')).data.data,
  })

  const refresh = (res: { message: string }) => {
    queryClient.invalidateQueries({ queryKey: ['admin-issue-reasons'] })
    queryClient.invalidateQueries({ queryKey: ['staff-issue-reasons'] })
    toast.success(res.message)
  }
  const fail = (title: string) => (err: unknown) => toast.error(title, errorMessage(err))

  const add = useMutation({
    mutationFn: async (body: { department_id: number; name: string }) =>
      (await api.post<{ message: string }>('/admin/issue-reasons', body)).data,
    onSuccess: (res) => {
      refresh(res)
      setName('')
    },
    onError: fail('Could not add reason'),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...body }: { id: number; name?: string; active?: boolean }) =>
      (await api.patch<{ message: string }>(`/admin/issue-reasons/${id}`, body)).data,
    onSuccess: (res, vars) => {
      refresh(res)
      if (vars.name !== undefined) {
        setEdits((prev) => {
          const next = { ...prev }
          delete next[vars.id]
          return next
        })
      }
    },
    onError: fail('Save failed'),
  })

  const reorder = useMutation({
    mutationFn: async (ids: number[]) => (await api.put<{ message: string }>('/admin/issue-reasons/reorder', { ids })).data,
    onSuccess: refresh,
    onError: fail('Reorder failed'),
  })

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete<{ message: string }>(`/admin/issue-reasons/${id}`)).data,
    onSuccess: (res) => {
      refresh(res)
      setDeleting(null)
    },
    onError: (err) => {
      setDeleting(null)
      fail('Could not delete')(err)
    },
  })

  if (departments.isLoading || reasons.isLoading) return <LogoLoader />

  const sections = departments.data?.departments ?? []
  const all = reasons.data ?? []
  const selectedId = departmentId ?? sections[0]?.id ?? null
  const selected = sections.find((d) => d.id === selectedId)
  const list = all.filter((reason) => reason.department_id === selectedId)

  const move = (index: number, direction: -1 | 1) => {
    const ids = list.map((reason) => reason.id)
    const target = index + direction
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target]!, ids[index]!]
    reorder.mutate(ids)
  }

  return (
    <div>
      <PageHeader title="Issue Reasons" subtitle="What each section's staff can pick when they flag a problem on a job" />

      <Card className="mb-5">
        <SectionLabel>Section</SectionLabel>
        <div className="flex flex-wrap gap-1.5">
          {sections.map((dept) => {
            const count = all.filter((reason) => reason.department_id === dept.id && reason.active).length
            const on = dept.id === selectedId
            return (
              <button
                key={dept.id}
                type="button"
                onClick={() => setDepartmentId(dept.id)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-semibold',
                  on ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:text-foreground',
                )}
              >
                {dept.sequence}. {dept.name}
                <span className={cn('ml-1.5 text-xs font-normal', on ? 'text-primary/80' : 'text-muted')}>{count}</span>
              </button>
            )
          })}
        </div>
      </Card>

      <Card className="mb-5">
        <SectionLabel>Add a reason for {selected?.name ?? 'this section'}</SectionLabel>
        <form
          className="grid gap-2 sm:flex"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim() && selectedId) add.mutate({ department_id: selectedId, name: name.trim() })
          }}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g. Artwork resolution too low"
            className="sm:flex-1"
          />
          <Button type="submit" loading={add.isPending} disabled={!name.trim() || !selectedId}>
            <Plus className="size-4" /> Add reason
          </Button>
        </form>
      </Card>

      <Table columns={['#', 'Reason', 'Used', 'Status', 'Order', actionsColumn]}>
        {list.length === 0 && (
          <EmptyRow colSpan={6}>No reasons for {selected?.name ?? 'this section'} - its staff type every issue under Other.</EmptyRow>
        )}
        {list.map((reason, index) => {
          const draft = edits[reason.id]
          const dirty = draft !== undefined && draft.trim() !== reason.name && draft.trim() !== ''
          const busy = update.isPending && update.variables?.id === reason.id

          return (
            <Row key={reason.id} className={cn(!reason.active && 'opacity-60')}>
              <Cell className="w-10 text-muted">{index + 1}</Cell>
              <Cell>
                <div className="flex min-w-56 items-center gap-1.5">
                  <Input
                    value={draft ?? reason.name}
                    maxLength={100}
                    onChange={(e) => setEdits({ ...edits, [reason.id]: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && dirty) update.mutate({ id: reason.id, name: draft.trim() })
                      if (e.key === 'Escape') setEdits({ ...edits, [reason.id]: reason.name })
                    }}
                  />
                  {dirty && (
                    <Button variant="ghost" className="px-2" loading={busy} onClick={() => update.mutate({ id: reason.id, name: draft.trim() })}>
                      Save
                    </Button>
                  )}
                </div>
              </Cell>
              <Cell className="text-muted">{reason.logs_count ?? 0}</Cell>
              <Cell>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => update.mutate({ id: reason.id, active: !reason.active })}
                  className={cn(
                    'rounded-lg border px-2.5 py-1 text-xs font-semibold',
                    reason.active ? 'border-success/60 bg-success/20 text-success' : 'border-foreground/30 bg-foreground/10 text-foreground/80',
                  )}
                  title={reason.active ? 'Click to hide from the staff picker' : 'Click to show in the staff picker'}
                >
                  {reason.active ? 'Active' : 'Inactive'}
                </button>
              </Cell>
              <Cell>
                <div className="flex items-center">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(index, -1)}
                    className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={index === list.length - 1 || reorder.isPending}
                    onClick={() => move(index, 1)}
                    className="-m-2 ml-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="size-4" />
                  </button>
                </div>
              </Cell>
              <Cell className={actionsCellClass}>
                <button
                  type="button"
                  aria-label={`Delete ${reason.name}`}
                  onClick={() => setDeleting(reason)}
                  className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-danger/10 hover:text-status-danger"
                >
                  <Trash2 className="size-4" />
                </button>
              </Cell>
            </Row>
          )
        })}
      </Table>

      <Card className="mt-5 border-primary/20 bg-primary/5">
        <p className="flex items-start gap-2 text-sm text-foreground/80">
          <Info className="mt-0.5 size-4 shrink-0 text-primary" />
          <span>
            When staff flag an issue they pick from their own section's active reasons, in this order, or choose "Other" for
            anything not listed. The same reason can be added to more than one section. Inactive reasons are hidden from the
            picker but stay on the issues that used them. A reason that has been used cannot be deleted - deactivate it
            instead so the reports keep grouping correctly.
          </span>
        </p>
      </Card>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title={deleting ? `Delete "${deleting.name}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        loading={remove.isPending}
      >
        It disappears from the {selected?.name ?? 'section'} picker.{' '}
        {(deleting?.logs_count ?? 0) > 0
          ? 'This reason has been used on flagged issues, so the server will refuse - deactivate it instead.'
          : 'It has not been used on any issue yet.'}
      </ConfirmDialog>
    </div>
  )
}
